import { api } from '@/lib/api/client';

export interface OverviewData {
  periodDays: number;
  conversations: {
    total: number;
    byState: Record<string, number>;
    resolutionRate: number;
    avgResolutionMinutes: number | null;
    escalationRate: number;
    escalated: number;
  };
  messages: {
    total: number;
    bySender: Record<string, number>;
  };
  customers: {
    new: number;
  };
}

export interface VolumePoint {
  date: string;
  total: number;
  resolved: number;
  escalated: number;
}

export interface MessageVolumePoint {
  date: string;
  customer: number;
  agent: number;
  staff: number;
}

export interface PriorityPoint {
  priority: string;
  count: number;
}

export interface StaffPerformance {
  staffId: string;
  name: string;
  conversations: number;
  resolved: number;
  messages: number;
  avgCsatScore: number | null;
  csatResponded: number;
}

export interface FeedbackSummary {
  periodDays: number;
  averageScore: number | null;
  responseRate: number;
  totalTriggered: number;
  totalResponded: number;
  byStatus: Record<string, number>;
  distribution: Record<number, number>; // 1..5 → count
}

function mapOverview(raw: Record<string, any>): OverviewData {
  const c = raw.conversations || {};
  const m = raw.messages || {};
  return {
    periodDays: raw.period_days,
    conversations: {
      total: c.total || 0,
      byState: c.by_state || {},
      resolutionRate: c.resolution_rate || 0,
      avgResolutionMinutes: c.avg_resolution_minutes,
      escalationRate: c.escalation_rate || 0,
      escalated: c.escalated || 0,
    },
    messages: {
      total: m.total || 0,
      bySender: m.by_sender || {},
    },
    customers: {
      new: raw.customers?.new || 0,
    },
  };
}

function mapStaffPerf(raw: Record<string, any>): StaffPerformance {
  return {
    staffId: raw.staff_id,
    name: raw.name,
    conversations: raw.conversations || 0,
    resolved: raw.resolved || 0,
    messages: raw.messages || 0,
    avgCsatScore: raw.avg_csat_score ?? null,
    csatResponded: raw.csat_responded || 0,
  };
}

function mapFeedbackSummary(raw: Record<string, any>): FeedbackSummary {
  // Distribution comes in as {"1": n, "2": n, ...} JSON keys (strings) —
  // coerce to number keys so the dashboard can iterate 1..5 cleanly.
  const dist: Record<number, number> = {};
  for (const [k, v] of Object.entries(raw.distribution || {})) {
    dist[Number(k)] = v as number;
  }
  return {
    periodDays: raw.period_days,
    averageScore: raw.average_score ?? null,
    responseRate: raw.response_rate ?? 0,
    totalTriggered: raw.total_triggered ?? 0,
    totalResponded: raw.total_responded ?? 0,
    byStatus: raw.by_status ?? {},
    distribution: dist,
  };
}

export const analyticsService = {
  async getOverview(days = 30): Promise<OverviewData> {
    const data = await api.get<Record<string, any>>(`/analytics/overview?days=${days}`);
    return mapOverview(data);
  },

  async getConversationVolume(days = 30): Promise<VolumePoint[]> {
    return api.get<VolumePoint[]>(`/analytics/conversations/volume?days=${days}`);
  },

  async getConversationsByPriority(days = 30): Promise<PriorityPoint[]> {
    return api.get<PriorityPoint[]>(`/analytics/conversations/by-priority?days=${days}`);
  },

  async getMessageVolume(days = 30): Promise<MessageVolumePoint[]> {
    return api.get<MessageVolumePoint[]>(`/analytics/messages/volume?days=${days}`);
  },

  async getStaffPerformance(days = 30): Promise<StaffPerformance[]> {
    const data = await api.get<Record<string, any>[]>(`/analytics/staff/performance?days=${days}`);
    return data.map(mapStaffPerf);
  },

  async getFeedbackSummary(days = 30): Promise<FeedbackSummary> {
    const data = await api.get<Record<string, any>>(`/analytics/feedback/summary?days=${days}`);
    return mapFeedbackSummary(data);
  },
};
